import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { If, Pagination } from 'components';
import { PayRunMemberJob } from '../confirmJob.model';
import { formatAmount, getAmountWithCurrency } from 'utils/amountUtils';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import { CheckboxTickIcon, GreyStrokeInfoIcon, HyphenIcon, SortByIcon, WarningIcon } from 'assets/icons';
import DateUtils from 'utils/dateUtils';
import JobReviewActions from './JobReviewActions';
import { getInvoiceStatus, getInvoiceStatusColor } from 'containers/invoices/utils';
import { InvoiceStatus } from 'containers/invoices/invoices.model';
import ExplainCommissionPanel from './ExplainCommissionPanel';
import TagPill from 'containers/tags/create-tag/Tag';
import { getSortedJobTags } from 'containers/jobs/utils';
import classNames from 'classnames';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import ExplainAdjustmentsPanel from './ExplainAdjustmentsPanel';
import { getSortTooltipText } from '../../utils';
import { ColumnInfo } from '../../pay-period-review/payperiod.model';
import { selectCurrentTeamAccountingPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { getCommissionBaseLabel } from 'containers/settings/utils';
import { selectIsFieldServePlus } from 'containers/subscriptions/modalSlice';
import { getStatusColor } from 'containers/estimates/utils';
import { EstimateStatus } from 'containers/estimates/constants';
import { getPaymentMethod } from 'containers/receipts/util';
interface Column {
  id: keyof PayRunMemberJob | 'actions' | 'checkbox';
  header:
    | string
    | ((props: { selectedJobs: PayRunMemberJob[]; jobs: PayRunMemberJob[]; onSelectAll: () => void }) => JSX.Element);
  accessor:
    | keyof PayRunMemberJob
    | ((
        data: PayRunMemberJob & {
          selectedJobs?: PayRunMemberJob[];
          onJobCheckboxSelect?: (jobData: PayRunMemberJob) => void;
        }
      ) => React.ReactNode);
  fixed?: 'left' | 'right';
  width?: string;
  tooltip?: string;
}

interface JobReviewTableProps {
  jobs: PayRunMemberJob[];
  selectedColumns?: string[];
  page: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  selectedJobs?: PayRunMemberJob[];
  onJobCheckboxSelect?: (job: PayRunMemberJob) => void;
  onSelectAll?: () => void;
  fetchData: (page: number) => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSortBy?: string;
  currentSortOrder?: string;
  columnInfo?: ColumnInfo[];
}

const rightFixedColumnSeperatorStyles = `first-letter:before:content-[""] before:absolute before:top-0 
 before:-left-[14px] before:bottom-0 before:w-[14px] before:bg-gradient-to-l from-[#162a400f] 
 to-transparent before:border-r before:border-borderGray`;

const leftFixedColumnSeperatorStyles = `first-letter:after:content-[""] after:absolute after:top-0 after:-right-[14px] 
 after:bottom-0 after:w-[14px] after:bg-gradient-to-r from-[#162a400f] to-transparent after:border-l 
 after:border-borderGray`;

const JobReviewTable: FC<JobReviewTableProps> = ({
  jobs,
  page,
  totalCount,
  onPageChange,
  selectedJobs = [],
  onJobCheckboxSelect,
  onSelectAll,
  fetchData,
  onSortChange,
  currentSortBy = 'scheduledDate',
  currentSortOrder = 'DESC',
  columnInfo
}) => {
  const navigate = useNavigate();
  const { payRunId, merchantId } = useParams();
  const { managePayAdjustments } = useAppSelector(selectCurrentTeamAccountingPermissions);
  const isFieldServePlus = useAppSelector(selectIsFieldServePlus);

  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [hoveredTooltipId, setHoveredTooltipId] = useState<string | null>(null);
  const [explainPanelData, setExplainPanelData] = useState<{
    isOpen: boolean;
    jobData?: PayRunMemberJob;
    type: 'commission' | 'adjustment';
  }>({
    isOpen: false,
    jobData: undefined,
    type: 'commission'
  });
  const [tableMinHeight, setTableMinHeight] = useState<string>('auto');

  // Calculate table min-height based on other elements
  useEffect(() => {
    const calculateTableHeight = () => {
      const elementIds = [
        'member-review-header',
        'member-review-summary-card',
        'member-review-tabs',
        'job-review-status-tabs',
        'job-review-filters',
        'job-review-table-actions',
        'pagination'
      ];

      let totalHeight = 0;

      elementIds.forEach(id => {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          totalHeight += rect.height;
        }
      });

      // Add some padding/margin buffer
      const buffer = 90;
      const calculatedMinHeight = `calc(100vh - ${totalHeight + buffer}px)`;
      setTableMinHeight(calculatedMinHeight);
    };

    // Calculate on mount and when window resizes
    calculateTableHeight();
    window.addEventListener('resize', calculateTableHeight);

    // Also recalculate when jobs change (in case it affects other element heights)
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener('resize', calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [jobs, totalCount]);

  // Helper function to format cell values
  const formatCellValue = (job, accessor) => {
    if (typeof accessor === 'function') {
      return accessor({
        ...job,
        selectedJobs,
        onJobCheckboxSelect
      });
    }

    // Check for null, undefined, or zero values
    return job[accessor] != null && job[accessor] !== '' && job[accessor] !== 0 ? String(job[accessor]) : '-';
  };

  const handleNavigateToJobReview = (jobId: string, status: string, e: React.MouseEvent) => {
    if (!isFieldServePlus) {
      return;
    }
    e.stopPropagation(); // Prevent row click handler from firing
    navigate(`/accounting/pay-runs/${payRunId}/members/${merchantId}/jobs/${jobId}`, {
      state: { jobReviewStatus: status }
    });
  };

  const getHeaderCheckBoxIcon = () => {
    if (selectedJobs.length === jobs.length && jobs.length > 0) {
      return <CheckboxTickIcon className="h-3 w-3 text-white" />;
    }
    if (selectedJobs.length > 0 && jobs.length > 0) {
      return <HyphenIcon className="h-3 w-3 text-white" />;
    }

    return null;
  };

  const columns: Column[] = [
    {
      id: 'checkbox',
      header: () => (
        <div className="flex cursor-pointer items-center gap-6 pl-[14px]">
          <CustomCheckbox
            id="select-all-jobs"
            className={selectedJobs.length > 0 || selectedJobs.length === jobs.length ? '' : 'bg-white'}
            isChecked={selectedJobs.length > 0 || selectedJobs.length === jobs.length}
            onClick={() => onSelectAll()}>
            {getHeaderCheckBoxIcon()}
          </CustomCheckbox>
        </div>
      ),
      accessor: data => (
        <div
          className="flex cursor-pointer items-center gap-6 pl-[14px]"
          onClick={e => {
            e.stopPropagation(); // Prevent row click handler
            data.onJobCheckboxSelect?.(data);
          }}>
          <CustomCheckbox
            className="shrink-0"
            id={`select-job-${data.jobId}`}
            isChecked={data.selectedJobs?.some(job => job.jobId === data.jobId)}
            onClick={() => data.onJobCheckboxSelect?.(data)}>
            {data.selectedJobs?.some(job => job.jobId === data.jobId) && (
              <CheckboxTickIcon className="h-3 w-3 text-white" />
            )}
          </CustomCheckbox>
        </div>
      ),
      fixed: 'left',
      width: 'w-[48px]'
    },
    {
      id: 'jobNumber',
      header: () => (
        <div className="flex cursor-pointer items-center pl-1">
          <span>Job ID</span>
        </div>
      ),
      accessor: data => (
        <div className="flex cursor-pointer items-center">
          <span className="whitespace-nowrap font-bold">{data.jobNumber.replaceAll('JOB-', '')}</span>
          <If condition={managePayAdjustments && data.hasDraftAdjustment}>
            <WarningIcon className="path-stroke-current ml-[10px] h-4 w-4 text-pending-orange" />
          </If>
        </div>
      ),
      fixed: 'left',
      width: 'w-[156px]'
    },
    {
      id: 'customerName',
      header: 'Customer',
      accessor: 'customerName',
      width: 'w-[200px]'
    },
    {
      id: 'scheduledDate',
      header: 'Date',
      accessor: data =>
        data.scheduledDate
          ? DateUtils.getDateInFormat({ date: new Date(data.scheduledDate), dateFormat: 'dd MMM yyyy' })
          : '-',
      width: 'w-[150px]'
    },
    {
      id: 'businessCost',
      header: 'Business costs',
      tooltip: 'Unit costs + Company expenses',
      accessor: data => (data.businessCost ? getAmountWithCurrency(data.businessCost) : '-'),
      width: 'w-[170px]'
    },
    {
      id: 'commissionAmount',
      header: 'Commission',
      accessor: data =>
        data.commissionAmount ? (
          <span className="flex flex-col items-start gap-2">
            {getAmountWithCurrency(data.commissionAmount)}
            <If condition={Boolean(Number(data.commissionAmount))}>
              <button
                className="cursor-pointer text-secondary"
                onClick={e => {
                  e.stopPropagation();
                  setExplainPanelData({ isOpen: true, jobData: data, type: 'commission' });
                }}>
                Explain
              </button>
            </If>
          </span>
        ) : (
          '-'
        ),
      width: 'w-[150px]'
    },
    {
      id: 'reimbursement',
      header: 'Team expense',
      tooltip: 'Team expenses reimbursable to this member',
      accessor: data => (data.reimbursement ? getAmountWithCurrency(data.reimbursement) : '-'),
      width: 'w-[200px]'
    },
    {
      id: 'adjustmentAmount',
      header: 'Adjustments',
      accessor: data =>
        data.adjustmentAmount ? (
          <span className="flex flex-col items-start gap-2">
            {getAmountWithCurrency(data.adjustmentAmount)}
            <If condition={data.jobAdjustmentCount > 0}>
              <button
                className="cursor-pointer text-secondary"
                onClick={e => {
                  e.stopPropagation();
                  setExplainPanelData({ isOpen: true, jobData: data, type: 'adjustment' });
                }}>
                {data.jobAdjustmentCount} {data.jobAdjustmentCount > 1 ? 'adjustments' : 'adjustment'}
              </button>
            </If>
          </span>
        ) : (
          '-'
        ),
      width: 'w-[150px]'
    },
    {
      id: 'grossPayAmount',
      header: 'Gross pay',
      tooltip: 'Balance of reimbursable expenses + Commission + Job adjustments',
      accessor: data => (data.grossPayAmount ? getAmountWithCurrency(data.grossPayAmount) : '-'),
      width: 'w-[150px]'
    },
    {
      id: 'invoiceAmount',
      header: 'Invoice total',
      accessor: data => (Number(data?.invoiceAmount) > 0 ? getAmountWithCurrency(data.invoiceAmount) : '-'),
      width: 'w-[150px]'
    },
    {
      id: 'paidAmount',
      header: 'Paid amount',
      accessor: data => (Number(data?.paidAmount) > 0 ? getAmountWithCurrency(data.paidAmount) : '-'),
      width: 'w-[150px]'
    },
    {
      id: 'commissionRate',
      header: 'Rate',
      accessor: data => (data.commissionRate ? `${formatAmount(data.commissionRate)}%` : '-'),
      width: 'w-[160px]'
    },
    {
      id: 'commissionRule',
      header: 'Rule',
      accessor: 'commissionRule',
      width: 'w-[150px]'
    },

    {
      id: 'commissionBase',
      header: 'Commission base',
      accessor: data =>
        data.commissionBase ? getCommissionBaseLabel(data.commissionBase) || data.commissionBase : '-',
      width: 'w-[170px]'
    },
    {
      id: 'commissionBaseAmount',
      header: 'Commission base amount',
      accessor: data =>
        data.commissionBaseAmount && data.commissionBase && data.commissionBase !== 'NA'
          ? getAmountWithCurrency(data.commissionBaseAmount)
          : '-',
      width: 'w-[215px]'
    },
    {
      id: 'invoiceId',
      header: 'Invoice ID',
      accessor: 'invoiceNumber',
      width: 'w-[150px]'
    },
    {
      id: 'invoiceStatus',
      header: 'Invoice status',
      accessor: job =>
        job.invoiceStatus ? (
          <div
            className="text-sbase font-bold"
            style={{ color: getInvoiceStatusColor(job.invoiceStatus as InvoiceStatus) }}>
            {getInvoiceStatus(job.invoiceStatus as InvoiceStatus)}
          </div>
        ) : (
          '-'
        ),
      width: 'w-[150px]'
    },
    {
      id: 'estimateStatus',
      header: 'Estimate status',
      accessor: job =>
        job.estimateStatus ? (
          <div
            className="text-sbase font-bold capitalize"
            style={{ color: getStatusColor(job.estimateStatus as EstimateStatus) }}>
            {job.estimateStatus?.toLowerCase()}
          </div>
        ) : (
          '-'
        ),
      width: 'w-[150px]'
    },
    {
      id: 'createdAt',
      header: 'Created On',
      accessor: job => (
        <span className="text-sbase text-primaryText">
          {DateUtils.getDateInFormat({ date: new Date(job.createdAt), dateFormat: 'dd MMM yyyy' })}
        </span>
      ),
      width: 'w-[150px]'
    },
    {
      id: 'paymentMethod',
      header: 'Payment method',
      accessor: data => {
        return data.paymentMethod ? (
          <>
            <div className="font-semibold">
              {getPaymentMethod({
                cardLast4: data.cardLast4,
                network: data.network,
                paymentMethod: data.paymentMethod,
                isManualKeyed: false
              })}
            </div>
            {data.transactionNote && (
              <div className="mt-1 text-sbase font-normal text-primaryText">{data.transactionNote}</div>
            )}
          </>
        ) : (
          '-'
        );
      },
      width: 'w-[150px]'
    },
    {
      id: 'serviceAddress',
      header: 'Service address',
      accessor: data => <div className="max-w-[250px] break-words">{data.serviceAddress}</div>,
      width: 'w-[250px]'
    },
    {
      id: 'tags',
      header: 'Tags',
      accessor: data => {
        return data.tags && data.tags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {getSortedJobTags(data.tags).map(tag => (
              <TagPill
                key={tag.id}
                tag={tag}
                showCloseButton={false}
                className="rounded-md px-1.5 py-0.5"
                textStyle="text-px13 font-semibold"
              />
            ))}
          </div>
        ) : (
          '-'
        );
      },
      width: 'w-[200px]'
    },
    {
      id: 'title',
      header: 'Job title',
      accessor: 'title',
      width: 'w-[200px]'
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: job =>
        isFieldServePlus ? (
          <JobReviewActions
            jobId={job.jobId}
            jobNumber={job.jobNumber}
            fetchData={fetchData}
            hasDraftAdjustment={job.hasDraftAdjustment}
          />
        ) : (
          '-'
        ),
      fixed: 'right',
      width: 'w-[100px]'
    }
  ];

  // Get visible columns based on columnInfo configuration
  const getVisibleColumns = () => {
    let result = [...columns];

    if (columnInfo && columnInfo.length > 0) {
      const enabledColumnKeys = columnInfo.filter(col => col.isEnabled).map(col => col.columnKey);

      result = result.filter(
        col =>
          enabledColumnKeys.includes(col.id) || col.id === 'checkbox' || col.id === 'actions' || col.id === 'jobNumber'
      );

      const checkboxCol = result.find(col => col.id === 'checkbox');
      const jobNumberCol = result.find(col => col.id === 'jobNumber');
      const actionsCol = result.find(col => col.id === 'actions');
      const otherCols = result.filter(col => !['jobNumber', 'actions', 'checkbox'].includes(col.id));

      const columnsToSort = [...otherCols];
      columnsToSort.sort((a, b) => {
        const aInfo = columnInfo.find(col => col.columnKey === a.id);
        const bInfo = columnInfo.find(col => col.columnKey === b.id);

        const aPriority = aInfo?.priority ?? 999;
        const bPriority = bInfo?.priority ?? 999;

        return aPriority - bPriority;
      });

      result = [
        ...(checkboxCol ? [checkboxCol] : []),
        ...(jobNumberCol ? [jobNumberCol] : []),
        ...columnsToSort,
        ...(actionsCol ? [actionsCol] : [])
      ];
    }

    return result;
  };

  const filteredColumns = getVisibleColumns();

  const leftFixedColumns = filteredColumns.filter(col => col.fixed === 'left');
  const rightFixedColumns = filteredColumns.filter(col => col.fixed === 'right');
  const scrollableColumns = filteredColumns.filter(col => !col.fixed);

  // Calculate cumulative left positions for fixed columns
  const getLeftPosition = (index: number) => {
    let position = 0;
    for (let i = 0; i < index; i++) {
      const col = leftFixedColumns[i];
      // Extract width value from Tailwind class (e.g., 'w-[50px]' -> 50)
      const widthMatch = col.width?.match(/w-\[(\d+)px\]/);
      if (widthMatch) {
        position += parseInt(widthMatch[1]);
      }
    }
    return position;
  };

  const sortableColumns: Array<keyof PayRunMemberJob> = [
    'jobNumber',
    'customerName',
    'scheduledDate',
    'invoiceAmount',
    'businessCost',
    'commissionRate',
    'commissionAmount',
    'reimbursement',
    'adjustmentAmount',
    'grossPayAmount',
    'paidAmount',
    'commissionBase',
    'commissionBaseAmount'
  ];

  const isSortable = (columnId: string): columnId is keyof PayRunMemberJob =>
    sortableColumns.includes(columnId as keyof PayRunMemberJob);

  const handleSortClick = (columnId: string) => {
    if (!onSortChange || !isSortable(columnId)) return;

    if (currentSortBy === columnId) {
      onSortChange(columnId, currentSortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      onSortChange(columnId, 'DESC');
    }
  };

  const getSortingProps = (columnId: string) => {
    const isActiveSort = currentSortBy === columnId;
    const shouldShowIcon = isActiveSort || hoveredColumn === columnId;
    const isTextColumn = ['customerName', 'jobNumber'].includes(columnId);
    const tooltipText = getSortTooltipText(columnId, currentSortBy, currentSortOrder, isTextColumn);
    const isColumnSortable = isSortable(columnId);
    return { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable };
  };

  const renderSortIcon = (isActiveSort: boolean, shouldShowIcon: boolean, isColumnSortable: boolean) => {
    if (!isColumnSortable) return null;
    const isDesc = currentSortOrder?.toUpperCase() === 'DESC';
    return (
      <div className="ml-1 cursor-pointer rounded-md py-1 px-1.5 hover:bg-gray-200">
        <SortByIcon
          className={`path path-fill-current rotate-180 transition-transform duration-200
              ${shouldShowIcon ? 'opacity-100' : 'opacity-0'} 
              ${isActiveSort ? 'text-primaryText' : 'text-accentText'} 
              ${isActiveSort && isDesc ? 'rotate-0' : ''}`}
        />
      </div>
    );
  };

  return (
    <>
      <div className="w-full">
        <div className="relative isolate mb-20 border border-gray-200">
          <div className="overflow-x-auto" style={{ minHeight: tableMinHeight }}>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  {/* Left Fixed Columns */}
                  {leftFixedColumns.map((column, index) => {
                    const { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable } = getSortingProps(column.id);
                    const leftPosition = getLeftPosition(index);
                    return (
                      <th
                        key={column.id}
                        className={`${column.width} sticky z-20 whitespace-nowrap 
                      bg-[#F4F4F4] px-4 py-3 text-left text-sbase font-semibold text-primaryText first-of-type:pr-0
                       ${index === leftFixedColumns.length - 1 ? leftFixedColumnSeperatorStyles : ''}`}
                        style={{ left: `${leftPosition}px` }}
                        onMouseEnter={() => setHoveredColumn(column.id)}
                        onMouseLeave={() => setHoveredColumn(null)}>
                        <TooltipWrapper
                          tooltip={tooltipText}
                          position="bottom"
                          showTooltip={isColumnSortable}
                          className="w-fit">
                          <div
                            className={classNames('flex max-w-fit items-center rounded-md', {
                              'hover:bg-gray-200 cursor-pointer': isColumnSortable
                            })}
                            onClick={() => isColumnSortable && handleSortClick(column.id)}>
                            {typeof column.header === 'function'
                              ? column.header({ selectedJobs, jobs, onSelectAll })
                              : column.header}
                            {renderSortIcon(isActiveSort, shouldShowIcon, isColumnSortable)}
                          </div>
                        </TooltipWrapper>
                      </th>
                    );
                  })}

                  {/* Scrollable Columns */}
                  <th className="bg-[#F4F4F4] p-0">
                    <div className="flex min-w-max flex-row">
                      {scrollableColumns.map(column => {
                        const { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable } = getSortingProps(
                          column.id
                        );
                        return (
                          <div
                            key={column.id}
                            className={classNames(
                              `${column.width} whitespace-nowrap px-4 py-3 
                            text-left text-sbase font-semibold text-primaryText`,
                              column.tooltip && 'flex items-center'
                            )}
                            onMouseEnter={() => setHoveredColumn(column.id)}
                            onMouseLeave={() => setHoveredColumn(null)}>
                            <TooltipWrapper
                              tooltip={tooltipText}
                              position="bottom"
                              showTooltip={isColumnSortable}
                              className="w-fit">
                              <div
                                className={classNames('flex max-w-fit items-center rounded-md pl-1', {
                                  'hover:bg-gray-200 cursor-pointer': isColumnSortable
                                })}
                                onClick={() => isColumnSortable && handleSortClick(column.id)}>
                                {typeof column.header === 'function'
                                  ? column.header({ selectedJobs, jobs, onSelectAll })
                                  : column.header}
                                {column.tooltip && (
                                  <div
                                    onMouseEnter={() => setHoveredTooltipId(column.id)}
                                    onMouseLeave={() => setHoveredTooltipId(null)}>
                                    <TooltipWrapper
                                      tooltip={column.tooltip}
                                      className='ml-1 cursor-pointer [&>[role="tooltip"]]:z-50'
                                      showTooltip={hoveredTooltipId === column.id}
                                      position="bottom">
                                      <GreyStrokeInfoIcon className="h-4 w-4" />
                                    </TooltipWrapper>
                                  </div>
                                )}
                                {renderSortIcon(isActiveSort, shouldShowIcon, isColumnSortable)}
                              </div>
                            </TooltipWrapper>
                          </div>
                        );
                      })}
                    </div>
                  </th>

                  {/* Right Fixed Columns */}
                  {rightFixedColumns.map(column => {
                    const { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable } = getSortingProps(column.id);
                    return (
                      <th
                        key={column.id}
                        className={`${column.width} sticky right-0 z-20 whitespace-nowrap
                      bg-[#F4F4F4] px-4 py-3 text-left text-sbase font-semibold text-primaryText
                       ${rightFixedColumnSeperatorStyles}`}
                        onMouseEnter={() => setHoveredColumn(column.id)}
                        onMouseLeave={() => setHoveredColumn(null)}>
                        <TooltipWrapper
                          tooltip={tooltipText}
                          position="bottom"
                          showTooltip={isColumnSortable}
                          className="w-fit">
                          <div
                            className={classNames('flex max-w-fit items-center rounded-md', {
                              'hover:bg-gray-200 cursor-pointer': isColumnSortable
                            })}
                            onClick={() => isColumnSortable && handleSortClick(column.id)}>
                            {typeof column.header === 'function'
                              ? column.header({ selectedJobs, jobs, onSelectAll })
                              : column.header}
                            {renderSortIcon(isActiveSort, shouldShowIcon, isColumnSortable)}
                          </div>
                        </TooltipWrapper>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {jobs.map((job, rowIndex) => (
                  <tr
                    key={job.jobId + rowIndex}
                    onClick={e => handleNavigateToJobReview(job.jobId, job.status, e)}
                    className="group cursor-pointer last-of-type:!border-b last-of-type:!border-gray-200">
                    {/* Left Fixed Columns */}
                    {leftFixedColumns.map((column, index) => {
                      const leftPosition = getLeftPosition(index);
                      return (
                        <td
                          key={column.id}
                          className={`${column.width} sticky z-10 bg-white p-4 
                          text-sbase font-semibold text-primaryText first-of-type:pr-0 group-hover:bg-gray-50
                          ${index === leftFixedColumns.length - 1 ? leftFixedColumnSeperatorStyles : ''}
                          ${rowIndex !== 0 ? 'border-t-2 border-gray-200' : ''} align-top`}
                          style={{ left: `${leftPosition}px` }}>
                          {formatCellValue(job, column.accessor)}
                        </td>
                      );
                    })}

                    {/* Scrollable Columns */}
                    <td className="bg-white p-0 group-hover:bg-gray-50">
                      <div className="inline-flex min-w-max">
                        {scrollableColumns.map(column => (
                          <div
                            key={column.id}
                            onClick={column.id === 'actions' ? e => e.stopPropagation() : undefined}
                            className={`${column.width} p-4 pl-5 text-sbase font-semibold text-primaryText
                             group-hover:bg-gray-50`}>
                            {formatCellValue(job, column.accessor)}
                          </div>
                        ))}
                      </div>
                    </td>

                    {/* Right Fixed Columns */}
                    {rightFixedColumns.map(column => (
                      <td
                        key={column.id}
                        onClick={e => e.stopPropagation()}
                        className={`${column.width} sticky right-0 z-10 bg-white p-4 
                        text-sbase font-semibold text-primaryText
                        ${rightFixedColumnSeperatorStyles}
                        ${rowIndex !== 0 ? 'border-t-2 border-gray-200' : ''} group-hover:bg-gray-50`}>
                        {formatCellValue(job, column.accessor)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Pagination currentPage={page} totalCount={totalCount} onPageChange={onPageChange} limit={50} />
      </div>

      {explainPanelData.jobData && explainPanelData.type === 'commission' && (
        <ExplainCommissionPanel
          isOpen={explainPanelData.isOpen}
          onClose={() => setExplainPanelData({ isOpen: false, jobData: undefined, type: 'commission' })}
          jobData={explainPanelData.jobData}
          merchantId={merchantId}
        />
      )}
      {explainPanelData.jobData && explainPanelData.type === 'adjustment' && (
        <ExplainAdjustmentsPanel
          isOpen={explainPanelData.isOpen}
          onClose={() => setExplainPanelData({ isOpen: false, jobData: undefined, type: 'adjustment' })}
          jobId={explainPanelData.jobData.jobId}
        />
      )}
    </>
  );
};

export default JobReviewTable;
